
import { forwardRef, useCallback, useEffect, useId, useRef } from 'react';
import type { RefObject } from 'react';
import { Button, Row, Spacer, Stack, Text } from '@client/shared/design';
import { AnchoredCard } from '@client/shared/design';
import { setRef } from '@client/shared/lib/set-ref.ts';
import styles from './ListingDetails.module.css';

export interface ArchiveConfirmPopoverProps {
  anchor: RefObject<HTMLElement | null>;
  busy: boolean;
  error: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ArchiveConfirmPopover = forwardRef<HTMLDivElement, ArchiveConfirmPopoverProps>(
  function ArchiveConfirmPopover({ anchor, busy, error, onConfirm, onCancel }, ref) {
    const titleId = useId();

    const cardRef = useRef<HTMLDivElement | null>(null);
    const setCardRef = useCallback(
      (node: HTMLDivElement | null) => {
        cardRef.current = node;
        setRef(ref, node);
      },
      [ref],
    );
    useEffect(() => {
      cardRef.current?.focus();
    }, []);

    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [onCancel]);

    return (
      <AnchoredCard
        ref={setCardRef}
        anchor={{ ref: anchor }}
        className={styles.actionCard}
        gap={8}
        align="end"
        flipRoom={200}
        flipUp={200}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <Stack gap={3}>
          <Text
            id={titleId}
            as="span"
            size="xs"
            tone="muted"
            weight="semibold"
            className={styles.popoverEyebrow}
          >
            Archive listing
          </Text>

          <Text size="sm" tone="soft">
            Archive this listing? It&rsquo;ll be hidden from browsing and can&rsquo;t be booked. You
            can unarchive it anytime.
          </Text>

          {error && (
            <Text size="sm" tone="danger">
              Couldn&rsquo;t archive &mdash; try again.
            </Text>
          )}

          <Row gap={2} align="center">
            <Spacer />
            <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" loading={busy} onClick={onConfirm}>
              Archive
            </Button>
          </Row>
        </Stack>
      </AnchoredCard>
    );
  },
);
